import { useEffect, useState } from 'react';
import { getUploadSheet, type ExcelSheet } from '../../../api/endpoints';

interface PreviewPanelProps {
  url: string;
  fileName: string;
  /** Excel の中身をテーブル表示するための識別子（xlsx のとき使用） */
  uploadId?: string;
  fileIndex?: number;
}

function ExcelSheetPreview({ uploadId, fileIndex, url, fileName }: { uploadId: string; fileIndex: number; url: string; fileName: string }) {
  const [sheets, setSheets] = useState<ExcelSheet[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSheets(null);
    setError(null);
    void (async () => {
      try {
        const res = await getUploadSheet(uploadId, fileIndex);
        if (!cancelled) setSheets(res.sheets);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [uploadId, fileIndex]);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="shrink-0 flex items-center justify-between px-2 py-1 border-b border-border text-xs text-muted">
        <span className="truncate" title={fileName}>{fileName}</span>
        <a href={url} className="text-primary hover:underline shrink-0 ml-2" download>ダウンロード</a>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-1">
        {error && <div className="text-xs text-danger p-2">プレビュー取得に失敗しました: {error}</div>}
        {!error && !sheets && <div className="text-xs text-muted p-2">読み込み中...</div>}
        {sheets?.map((sheet, si) => (
          <div key={si} className="mb-3">
            {sheets.length > 1 && <div className="text-xs font-medium text-muted mb-1">シート: {sheet.name}</div>}
            <table className="border-collapse text-xs">
              <tbody>
                {sheet.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="border border-border px-1 py-0.5 whitespace-nowrap align-top max-w-[220px] overflow-hidden text-ellipsis" title={cell}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

/** プレビューを別ウィンドウ（ポップアップ）で開く。ブロックされたら同タブで開く。 */
function openInNewWindow(url: string) {
  const win = window.open(url, '_blank', 'noopener,noreferrer,width=900,height=1200');
  if (!win) window.open(url, '_blank');
}

export function PreviewPanel({ url, fileName, uploadId, fileIndex = 0 }: PreviewPanelProps) {
  const lower = fileName.toLowerCase();
  const isPdf = lower.endsWith('.pdf');
  const isExcel = lower.endsWith('.xlsx');
  const isImage = !isPdf && !isExcel;

  // 画像プレビューのズーム倍率（PDFはビューア内蔵ズームを使う）。ファイル切替でリセット。
  const [zoom, setZoom] = useState(1);
  useEffect(() => { setZoom(1); }, [url]);

  if (isExcel) {
    return (
      <div className="border border-border rounded flex-1 min-h-0 flex flex-col">
        {uploadId ? (
          <ExcelSheetPreview uploadId={uploadId} fileIndex={fileIndex} url={url} fileName={fileName} />
        ) : (
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 text-center p-4 text-muted">
            <span className="text-4xl">📄</span>
            <div className="text-sm">Excelはプレビュー表示できません</div>
            <div className="text-xs break-all">{fileName}</div>
            <a href={url} className="text-xs text-primary hover:underline" download>ダウンロード</a>
          </div>
        )}
      </div>
    );
  }

  const btn = 'text-xs px-1.5 py-0.5 rounded border border-border text-slate-600 hover:bg-slate-100 transition-colors';
  return (
    <div className="border border-border rounded flex-1 min-h-0 flex flex-col">
      {/* ツールバー: 「プレビュー」見出し＋画像ズーム＋別ウィンドウを1行に集約（縦幅節約） */}
      <div className="shrink-0 flex items-center gap-1 px-2 py-1 border-b border-border">
        <span className="text-sm font-medium mr-1">プレビュー</span>
        {isImage && (
          <>
            <button type="button" className={btn} title="縮小" aria-label="縮小"
              onClick={() => setZoom((z) => Math.max(0.2, +(z - 0.2).toFixed(2)))}>−</button>
            <span className="text-xs text-muted w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
            <button type="button" className={btn} title="拡大" aria-label="拡大"
              onClick={() => setZoom((z) => Math.min(5, +(z + 0.2).toFixed(2)))}>＋</button>
            <button type="button" className={btn} title="等倍に戻す"
              onClick={() => setZoom(1)}>リセット</button>
          </>
        )}
        <button type="button" className={`${btn} ml-auto`} title="別ウィンドウで開く"
          onClick={() => openInNewWindow(url)}>↗ 別ウィンドウ</button>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {isPdf ? (
          <iframe src={`${url}#view=FitH&toolbar=1`} className="w-full h-full" title="preview" />
        ) : (
          <img src={url} style={{ width: `${zoom * 100}%` }} className="max-w-none block mx-auto" alt="preview" />
        )}
      </div>
    </div>
  );
}
